import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCircle, XCircle, ShieldCheck, Receipt, Check, Scales, WarningCircle } from "@phosphor-icons/react";

const ICONS = {
  journal_posted: { icon: Receipt, cls: "text-positive" },
  journal_failed: { icon: XCircle, cls: "text-negative" },
  approval_requested: { icon: ShieldCheck, cls: "text-orangeink" },
  approval_done: { icon: CheckCircle, cls: "text-positive" },
  approval_rejected: { icon: XCircle, cls: "text-negative" },
  variance_new: { icon: Scales, cls: "text-orangeink" },
  close_overdue: { icon: WarningCircle, cls: "text-negative" },
};

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return d === 1 ? "yesterday" : `${d}d ago`;
}

export function NotificationBell({ collapsed }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const h = () => load();
    window.addEventListener("notifications:refresh", h);
    return () => { clearInterval(t); window.removeEventListener("notifications:refresh", h); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await api.post("/notifications/read", {});
        setUnread(0);
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch { /* ignore */ }
    }
  };

  const go = (n) => { setOpen(false); if (n.link) navigate(n.link); };

  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notification-bell" title="Notifications" aria-label="Notifications"
        className={`relative flex items-center justify-center rounded-lg text-white/60 bg-white/5 hover:bg-white/10 hover:text-white transition-colors ${collapsed ? "w-full py-2" : "w-10 h-10"}`}>
        <Bell size={collapsed ? 20 : 18} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span data-testid="notification-unread-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-orange text-white text-[0.6rem] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notification-panel"
            style={{ left: collapsed ? 88 : 268, top: 64 }}
            className="fixed w-80 max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-slate-200 z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
              <span className="font-heading font-bold text-navy">Notifications</span>
              {items.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[0.65rem] text-positive font-semibold"><Check size={12} weight="bold" /> Up to date</span>
              )}
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-navy/55" data-testid="notification-empty">
                <Bell size={28} className="mx-auto mb-2 text-navy/25" />
                Nothing yet — journal and approval activity will show up here.
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {items.map((n) => {
                  const meta = ICONS[n.type] || { icon: Bell, cls: "text-navy/50" };
                  const Icon = meta.icon;
                  return (
                    <button key={n.id} onClick={() => go(n)} data-testid={`notification-item-${n.id}`}
                      className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-orange/5"}`}>
                      <Icon size={18} weight="fill" className={`${meta.cls} shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-navy">{n.title}</div>
                        {n.body && <p className="text-xs text-navy/60 mt-0.5 line-clamp-2">{n.body}</p>}
                        <span className="text-[0.65rem] text-navy/40">{ago(n.at)}</span>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-orange shrink-0 mt-1.5" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
